package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.model.ActiveWhiteboard
import fr.manyverse.app.domain.model.WhiteboardAction
import fr.manyverse.app.domain.repository.WhiteboardRepository
import fr.manyverse.app.infrastructure.error.mapToDomainException
import fr.manyverse.app.infrastructure.realtime.GameChange
import fr.manyverse.app.infrastructure.realtime.GameChannelEvent
import fr.manyverse.app.infrastructure.realtime.GameRealtimeChannel
import fr.manyverse.app.infrastructure.serialization.appJson
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Columns
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.channelFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapNotNull
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlin.time.Clock
import kotlin.time.Instant

private const val TAG = "WhiteboardRepository"
private const val WHITEBOARDS_TABLE = "game_table_whiteboards"
private const val WHITEBOARD_ACTIONS_TABLE = "game_table_whiteboard_actions"

/**
 * Supabase implementation of WhiteboardRepository.
 * Handles whiteboard operations with real-time action broadcasting.
 */
class SupabaseWhiteboardRepository(
    private val supabaseClient: SupabaseClient,
    private val gameChannel: GameRealtimeChannel
) : WhiteboardRepository {

    private val json = appJson {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }

    override suspend fun createWhiteboard(
        tableId: String,
        createdBy: String,
        targetParticipants: List<String>,
        showGrid: Boolean
    ): Result<ActiveWhiteboard> {
        return try {
            val insertDto = WhiteboardInsertDto(
                tableId = tableId,
                createdBy = createdBy,
                targetParticipants = targetParticipants,
                isActive = true,
                showGrid = showGrid
            )

            val whiteboard = supabaseClient
                .from(WHITEBOARDS_TABLE)
                .insert(insertDto) {
                    select()
                }
                .decodeSingle<WhiteboardDto>()

            Result.success(whiteboard.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to create whiteboard for table=$tableId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun broadcastAction(
        tableId: String,
        whiteboardId: String,
        action: WhiteboardAction,
        performedBy: String
    ): Result<Unit> {
        return try {
            val actionJson = json.encodeToString(WhiteboardAction.serializer(), action)
            val actionType = when (action) {
                is WhiteboardAction.Stroke -> "stroke"
                is WhiteboardAction.Clear -> "clear"
                is WhiteboardAction.ToggleGrid -> "toggle_grid"
                is WhiteboardAction.CloseForAll -> "close_for_all"
            }

            val insertDto = WhiteboardActionInsertDto(
                whiteboardId = whiteboardId,
                tableId = tableId,
                actionType = actionType,
                actionData = actionJson,
                performedBy = performedBy
            )

            supabaseClient
                .from(WHITEBOARD_ACTIONS_TABLE)
                .insert(insertDto)

            Result.success(Unit)
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to broadcast action for whiteboard=$whiteboardId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override fun observeWhiteboardActions(
        tableId: String,
        whiteboardId: String
    ): Flow<WhiteboardAction> = channelFlow {
        gameChannel.events(WHITEBOARD_ACTIONS_TABLE).collect { event ->
            when (event) {
                // Le tableau est reconstruit depuis l'historique à chaque (re)prise de l'écoute : les
                // tracés faits avant, ou pendant une coupure, ne sont pas rejoués. À l'ouverture, il
                // redessine au pire ce que l'appelant vient de charger. En cas d'échec (déjà remonté
                // par le mapper), le contenu affiché reste
                GameChannelEvent.Ready -> getWhiteboardActions(whiteboardId).onSuccess { history ->
                    resyncActions(history).forEach { send(it) }
                }

                is GameChannelEvent.Change -> {
                    val record = event.change.record ?: return@collect
                    if (event.change.type != GameChange.Type.INSERT) return@collect
                    val action = try {
                        parseActionFromRecord(record, whiteboardId)
                    } catch (e: Exception) {
                        AppLogger.error(TAG, "Failed to parse action from real-time update", e)
                        null
                    }
                    action?.let { send(it) }
                }
            }
        }
    }

    override suspend fun getActiveWhiteboard(
        tableId: String
    ): Result<ActiveWhiteboard?> {
        return try {
            val whiteboards = supabaseClient
                .from(WHITEBOARDS_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("table_id", tableId)
                        eq("is_active", true)
                    }
                }
                .decodeList<WhiteboardDto>()

            Result.success(whiteboards.firstOrNull()?.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to get active whiteboard for table=$tableId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun closeWhiteboard(
        whiteboardId: String
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(WHITEBOARDS_TABLE)
                .update({
                    set("is_active", false)
                }) {
                    filter {
                        eq("id", whiteboardId)
                    }
                }

            Result.success(Unit)
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to close whiteboard=$whiteboardId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun deleteWhiteboardActions(
        whiteboardId: String
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(WHITEBOARD_ACTIONS_TABLE)
                .delete {
                    filter {
                        eq("whiteboard_id", whiteboardId)
                    }
                }
            Result.success(Unit)
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to delete actions for whiteboard=$whiteboardId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun updateGridState(
        whiteboardId: String,
        showGrid: Boolean
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(WHITEBOARDS_TABLE)
                .update({
                    set("show_grid", showGrid)
                }) {
                    filter {
                        eq("id", whiteboardId)
                    }
                }

            Result.success(Unit)
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to update grid state for whiteboard=$whiteboardId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun getWhiteboardForTable(
        tableId: String
    ): Result<ActiveWhiteboard?> {
        return try {
            val whiteboards = supabaseClient
                .from(WHITEBOARDS_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("table_id", tableId)
                    }
                    order("created_at", Order.DESCENDING)
                    limit(1)
                }
                .decodeList<WhiteboardDto>()

            Result.success(whiteboards.firstOrNull()?.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to get whiteboard for table=$tableId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun reactivateWhiteboard(
        whiteboardId: String,
        targetParticipants: List<String>,
        showGrid: Boolean
    ): Result<ActiveWhiteboard> {
        return try {
            val whiteboard = supabaseClient
                .from(WHITEBOARDS_TABLE)
                .update({
                    set("is_active", true)
                    set("target_participants", targetParticipants)
                    set("show_grid", showGrid)
                }) {
                    filter {
                        eq("id", whiteboardId)
                    }
                    select()
                }
                .decodeSingle<WhiteboardDto>()

            Result.success(whiteboard.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to reactivate whiteboard=$whiteboardId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun getWhiteboardActions(
        whiteboardId: String
    ): Result<List<WhiteboardAction>> {
        return try {
            val actions = supabaseClient
                .from(WHITEBOARD_ACTIONS_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("whiteboard_id", whiteboardId)
                    }
                    order("created_at", Order.ASCENDING)
                }
                .decodeList<WhiteboardActionReadDto>()
                .mapNotNull { it.toDomain(json) }

            Result.success(actions)
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to get whiteboard actions for whiteboard=$whiteboardId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override fun observeActiveWhiteboards(
        tableId: String,
        currentUserId: String
    ): Flow<ActiveWhiteboard?> = gameChannel.changes(WHITEBOARDS_TABLE).mapNotNull { change ->
        val record = change.record ?: return@mapNotNull null
        try {
            when (change.type) {
                GameChange.Type.INSERT ->
                    parseWhiteboardFromRecord(record, tableId, currentUserId)?.let { WhiteboardUpdate(it) }

                GameChange.Type.UPDATE -> when {
                    extractString(record["table_id"]) != tableId -> null
                    !extractStringList(json, record["target_participants"]).contains(currentUserId) -> null
                    // Tableau fermé : null le ferme chez l'utilisateur
                    extractBoolean(record["is_active"]) != true -> WhiteboardUpdate(null)
                    else -> parseWhiteboardFromRecord(record, tableId, currentUserId)?.let { WhiteboardUpdate(it) }
                }

                GameChange.Type.DELETE -> null
            }
        } catch (e: Exception) {
            AppLogger.error(TAG, "Failed to parse whiteboard from real-time update", e)
            null
        }
    }.map { it.whiteboard }

    /**
     * Wrapper to distinguish between emitting a whiteboard and emitting null (close).
     */
    private data class WhiteboardUpdate(val whiteboard: ActiveWhiteboard?)

    private fun parseWhiteboardFromRecord(
        record: Map<String, Any?>,
        expectedTableId: String,
        currentUserId: String
    ): ActiveWhiteboard? {
        val tableId = extractString(record["table_id"]) ?: return null

        // Only process whiteboards for the expected table
        if (tableId != expectedTableId) {
            return null
        }

        val isActive = extractBoolean(record["is_active"]) ?: false
        if (!isActive) {
            return null
        }

        val targetParticipants = extractStringList(json, record["target_participants"])

        // Only emit if current user is a target
        if (!targetParticipants.contains(currentUserId)) {
            return null
        }

        val id = extractString(record["id"]) ?: return null
        val createdBy = extractString(record["created_by"]) ?: return null
        val showGrid = extractBoolean(record["show_grid"]) ?: false
        val createdAtStr = extractString(record["created_at"]) ?: return null
        val createdAt = try {
            Instant.parse(createdAtStr)
        } catch (e: Exception) {
            Clock.System.now()
        }

        return ActiveWhiteboard(
            id = id,
            tableId = tableId,
            createdBy = createdBy,
            targetParticipants = targetParticipants,
            isActive = isActive,
            showGrid = showGrid,
            createdAt = createdAt
        )
    }


    private fun parseActionFromRecord(
        record: Map<String, Any?>,
        expectedWhiteboardId: String
    ): WhiteboardAction? {
        val whiteboardId = extractString(record["whiteboard_id"]) ?: return null

        // Only process actions for the expected whiteboard
        if (whiteboardId != expectedWhiteboardId) {
            return null
        }

        val actionDataRaw = record["action_data"] ?: return null
        return try {
            val actionDataStr = when (actionDataRaw) {
                is JsonPrimitive -> actionDataRaw.content
                is JsonObject -> actionDataRaw.toString()
                is String -> actionDataRaw
                else -> actionDataRaw.toString()
            }
            json.decodeFromString<WhiteboardAction>(actionDataStr)
        } catch (e: Exception) {
            AppLogger.error(TAG, "Failed to parse whiteboard action (${actionDataRaw::class.simpleName})", e)
            null
        }
    }

    @Serializable
    private data class WhiteboardActionReadDto(
        @SerialName("whiteboard_id")
        val whiteboardId: String,

        @SerialName("action_type")
        val actionType: String,

        @SerialName("action_data")
        val actionData: JsonElement
    ) {
        fun toDomain(json: Json): WhiteboardAction? = try {
            val str = when (actionData) {
                is JsonPrimitive -> actionData.content
                else -> actionData.toString()
            }
            json.decodeFromString<WhiteboardAction>(str)
        } catch (e: Exception) {
            null
        }
    }

    /**
     * DTO for inserting whiteboards into Supabase.
     */
    @Serializable
    private data class WhiteboardInsertDto(
        @SerialName("table_id")
        val tableId: String,

        @SerialName("created_by")
        val createdBy: String,

        @SerialName("target_participants")
        val targetParticipants: List<String>,

        @SerialName("is_active")
        val isActive: Boolean,

        @SerialName("show_grid")
        val showGrid: Boolean
    )

    /**
     * DTO for deserializing whiteboards from Supabase.
     */
    @Serializable
    private data class WhiteboardDto(
        @SerialName("id")
        val id: String,

        @SerialName("table_id")
        val tableId: String,

        @SerialName("created_by")
        val createdBy: String,

        @SerialName("target_participants")
        val targetParticipants: List<String>,

        @SerialName("is_active")
        val isActive: Boolean,

        @SerialName("show_grid")
        val showGrid: Boolean,

        @SerialName("created_at")
        val createdAt: Instant
    ) {
        fun toDomain(): ActiveWhiteboard = ActiveWhiteboard(
            id = id,
            tableId = tableId,
            createdBy = createdBy,
            targetParticipants = targetParticipants,
            isActive = isActive,
            showGrid = showGrid,
            createdAt = createdAt
        )
    }

    /**
     * DTO for inserting whiteboard actions into Supabase.
     */
    @Serializable
    private data class WhiteboardActionInsertDto(
        @SerialName("whiteboard_id")
        val whiteboardId: String,

        @SerialName("table_id")
        val tableId: String,

        @SerialName("action_type")
        val actionType: String,

        @SerialName("action_data")
        val actionData: String,

        @SerialName("performed_by")
        val performedBy: String
    )
}

/**
 * Actions qui ramènent un tableau au contenu de son [history] : un effacement, puis les tracés
 * postérieurs au dernier effacement. Une fermeture passée compte comme un effacement : déjà
 * appliquée, la rejouer fermerait le tableau. La grille vient des métadonnées du tableau.
 */
internal fun resyncActions(history: List<WhiteboardAction>): List<WhiteboardAction> {
    val lastReset = history.indexOfLast {
        it is WhiteboardAction.Clear || it is WhiteboardAction.CloseForAll
    }
    return listOf(WhiteboardAction.Clear) +
        history.drop(lastReset + 1).filterIsInstance<WhiteboardAction.Stroke>()
}
